import React, { useEffect, useMemo, useState } from "react";
import { Package, Rocket, RefreshCw, AlertTriangle, CheckCircle2, Loader2, Clock, Layers, Beaker, ArrowUpRight, Settings as SettingsIcon } from "lucide-react";
import { http, errMsg } from "@/lib/api";
import { toast } from "sonner";

/**
 * SuperAdminReleases — fleet-wide release management for the platform owner.
 *
 * Shows every uploaded release with two key columns:
 *  - `Fleet below` — count of enabled agents on a strictly lower version
 *  - `Fleet at/above` — count of enabled agents that are already current
 *
 * Per row there's a single "Roll out" button that POSTs to
 * `/api/v1/agents/releases/{version}/rollout` (superadmin). The backend
 * enqueues an `update-now` command on each below-target agent; v1.0.24+
 * agents pick it up within ~5 s and self-update immediately, bypassing
 * the default 6-hour UpdateService poll.
 */
export default function SuperAdminReleases() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [rollingOut, setRollingOut] = useState("");

    const load = async () => {
        try {
            const { data } = await http.get("/v1/agents/releases/admin");
            setData(data);
        } catch (e) {
            setErr(errMsg(e, "Failed to load releases"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const fleetTotal = data?.fleet_total || 0;
    const fleetCanary = data?.fleet_canary || 0;
    const fleetStable = data?.fleet_stable || 0;
    const settings = data?.settings || {};
    const canaryMetrics = data?.canary_metrics || {};
    const releases = data?.releases || [];

    const latest = useMemo(() => releases[0] || null, [releases]);

    const rollout = async (version, fleetBelow) => {
        const ok = window.confirm(
            `Roll out v${version} to ${fleetBelow} agent(s) below it?\n\n` +
            `v1.0.24+ agents will self-update within ~5 seconds.\n` +
            `Older agents will need a manual restart to pick up the new version.`,
        );
        if (!ok) return;
        setRollingOut(version);
        try {
            const { data: res } = await http.post(`/v1/agents/releases/${encodeURIComponent(version)}/rollout`);
            toast.success(
                `Rollout queued for ${res.agents_targeted} agent(s) across ${res.tenants} tenant(s).`,
            );
            if (res.agents_legacy > 0) {
                toast.warning(
                    `${res.agents_legacy} agent(s) are below v1.0.24 — restart them manually to apply the update.`,
                );
            }
            await load();
        } catch (e) {
            toast.error(errMsg(e, "Rollout failed"));
        } finally {
            setRollingOut("");
        }
    };

    const promote = async (version, health, force = false) => {
        if (!force && !health?.eligible_for_promotion) {
            toast.error(`Cannot promote yet — ${health?.reason || "gates not met"}`);
            return;
        }
        const label = force ? "FORCE-promote" : "promote";
        if (!window.confirm(`${label} v${version} from canary → stable?`)) return;
        try {
            const url = `/v1/agents/releases/${encodeURIComponent(version)}/promote-from-canary${force ? "?force=true" : ""}`;
            await http.post(url);
            toast.success(`v${version} promoted to stable.`);
            await load();
        } catch (e) {
            toast.error(errMsg(e, "Promotion failed"));
        }
    };

    return (
        <div className="px-6 py-6" data-testid="superadmin-releases-page">
            <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.28em] text-[#00C2FF]/80">
                        <Package size={12} /> Agent Releases
                    </div>
                    <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">
                        Fleet Rollout
                    </h1>
                    <p className="mt-1 max-w-2xl text-sm text-white/55">
                        Push a new agent build to every device below it with one click. Targets
                        only enabled agents and never restarts an in-flight migration mid-job.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => { setLoading(true); load(); }}
                    className="inline-flex items-center gap-1.5 rounded-md border border-white/10 bg-white/[0.04] px-3 py-2 text-xs font-semibold uppercase tracking-widest text-white/70 transition hover:border-white/30 hover:text-white"
                    data-testid="superadmin-releases-refresh"
                >
                    <RefreshCw size={12} /> Refresh
                </button>
            </header>

            {/* Fleet overview strip */}
            <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="superadmin-releases-overview">
                <StatCard
                    icon={<Layers size={16} />}
                    label="Enabled agents"
                    value={fleetTotal}
                    sub={`${fleetStable} stable · ${fleetCanary} canary`}
                    testId="superadmin-releases-fleet-total"
                />
                <StatCard
                    icon={<Rocket size={16} />}
                    label="Latest stable"
                    value={latest ? `v${latest.version}` : "—"}
                    testId="superadmin-releases-latest"
                />
                <StatCard
                    icon={<Beaker size={16} />}
                    label="Canary share"
                    value={`${settings.canary_percent ?? 0}%`}
                    sub={settings.canary_tenant_id ? "+ pinned tenant" : "auto-hashed"}
                    testId="superadmin-releases-canary-pct"
                />
                <StatCard
                    icon={<Clock size={16} />}
                    label="Default check interval"
                    value="6 h"
                    sub="`update-now` bypasses this"
                    testId="superadmin-releases-interval"
                />
            </div>

            {/* Canary settings drawer */}
            <CanarySettingsCard settings={settings} onSaved={load} />

            {err && (
                <div className="mb-4 rounded-md border border-[#ef4444]/40 bg-[#ef4444]/10 px-3 py-2 text-sm text-[#fca5a5]" data-testid="superadmin-releases-error">
                    {err}
                </div>
            )}

            {loading ? (
                <div className="flex items-center justify-center gap-2 py-16 text-sm text-white/60">
                    <Loader2 size={14} className="animate-spin" /> Loading releases…
                </div>
            ) : (
                <div className="overflow-hidden rounded-xl border border-white/10 bg-white/[0.03]" data-testid="superadmin-releases-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-white/10 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">
                                <th className="px-4 py-2.5">Version</th>
                                <th className="px-4 py-2.5">Channel</th>
                                <th className="px-4 py-2.5">Released</th>
                                <th className="px-4 py-2.5 text-right">Fleet below</th>
                                <th className="px-4 py-2.5 text-right">At / above</th>
                                <th className="px-4 py-2.5 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {releases.length === 0 ? (
                                <tr><td colSpan={6} className="px-4 py-10 text-center text-white/55">
                                    No releases uploaded yet.
                                </td></tr>
                            ) : releases.map((r) => (
                                <ReleaseRow
                                    key={r.version}
                                    release={r}
                                    busy={rollingOut === r.version}
                                    health={canaryMetrics[r.version]}
                                    onRollout={() => rollout(r.version, r.fleet_below)}
                                    onPromote={(force) => promote(r.version, canaryMetrics[r.version], force)}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="mt-4 flex items-start gap-2 rounded-md border border-[#f59e0b]/30 bg-[#f59e0b]/[0.05] p-3 text-[12px] text-white/65" data-testid="superadmin-releases-legacy-warning">
                <AlertTriangle size={14} className="mt-0.5 shrink-0 text-[#f59e0b]" />
                <span>
                    <b>Legacy agents (&lt; v1.0.24)</b> don&apos;t recognise the <code className="rounded bg-white/10 px-1 py-0.5 font-mono text-[11px]">update-now</code> command yet.
                    The rollout still enqueues it for them (they ack and discard), but you&apos;ll need a manual restart
                    OR wait for their next 6-hour <code className="rounded bg-white/10 px-1 py-0.5 font-mono text-[11px]">/v1/agents/update</code> poll to pull the new build.
                    Once every agent is on v1.0.24+, rollouts are fully one-click.
                </span>
            </div>
        </div>
    );
}

function StatCard({ icon, label, value, sub, testId }) {
    return (
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid={testId}>
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-white/45">
                {icon} {label}
            </div>
            <div className="mt-1.5 font-display text-2xl font-bold tracking-tight">{value}</div>
            {sub && <div className="mt-0.5 font-mono text-[10px] text-white/40">{sub}</div>}
        </div>
    );
}

function ReleaseRow({ release, busy, health, onRollout, onPromote }) {
    const noTargets = (release.fleet_below || 0) === 0;
    const isCanary = release.channel === "canary";
    const channelStyle =
        release.channel === "stable" ? "border-[#00C2FF]/40 bg-[#00C2FF]/10 text-[#7fe0ff]"
        : release.channel === "canary" ? "border-[#a78bfa]/40 bg-[#a78bfa]/10 text-[#c4b5fd]"
        : "border-[#f59e0b]/40 bg-[#f59e0b]/10 text-[#fcd34d]";
    return (
        <tr
            className="border-b border-white/[0.06] last:border-b-0 hover:bg-white/[0.02]"
            data-testid={`superadmin-release-row-${release.version}`}
        >
            <td className="px-4 py-3 font-mono text-[13px] font-semibold">v{release.version}</td>
            <td className="px-4 py-3">
                <span className={`rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em] ${channelStyle}`}>
                    {release.channel}
                </span>
                {isCanary && health && (
                    <CanaryHealthChip health={health} />
                )}
            </td>
            <td className="px-4 py-3 font-mono text-[11px] text-white/55">
                {release.released_at ? new Date(release.released_at).toLocaleString() : "—"}
            </td>
            <td className="px-4 py-3 text-right font-mono text-[13px]">
                {noTargets ? (
                    <span className="inline-flex items-center gap-1 text-white/40">
                        <CheckCircle2 size={11} /> 0
                    </span>
                ) : (
                    <span className="text-[#fcd34d]">{release.fleet_below}</span>
                )}
            </td>
            <td className="px-4 py-3 text-right font-mono text-[13px] text-white/60">
                {release.fleet_at_or_above}
            </td>
            <td className="px-4 py-3 text-right">
                {isCanary ? (
                    <div className="inline-flex items-center gap-1.5">
                        <button
                            type="button"
                            onClick={() => onPromote(false)}
                            disabled={!health?.eligible_for_promotion}
                            data-testid={`superadmin-release-promote-${release.version}`}
                            title={health?.eligible_for_promotion ? "Promote to stable channel" : health?.reason || "Gates not met"}
                            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-r from-[#7c3aed] to-[#a78bfa] px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-white shadow-lg shadow-[#a78bfa]/25 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-30"
                        >
                            <ArrowUpRight size={11} />
                            Promote
                        </button>
                        <button
                            type="button"
                            onClick={() => onPromote(true)}
                            data-testid={`superadmin-release-promote-force-${release.version}`}
                            title="FORCE promotion (skip health gates)"
                            className="inline-flex items-center rounded-md border border-[#f59e0b]/40 px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#fcd34d] hover:bg-[#f59e0b]/10"
                        >
                            Force
                        </button>
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={onRollout}
                        disabled={busy || noTargets || !release.published}
                        data-testid={`superadmin-release-rollout-${release.version}`}
                        title={!release.published ? "Release not published" : noTargets ? "All agents already on or above this version" : ""}
                        className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-r from-[#004AAD] to-[#00C2FF] px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-white shadow-lg shadow-[#00C2FF]/20 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-30"
                    >
                        {busy ? <Loader2 size={11} className="animate-spin" /> : <Rocket size={11} />}
                        Roll out
                    </button>
                )}
            </td>
        </tr>
    );
}

/* ---- Canary health chip — inline under the channel pill ----------------- */
function CanaryHealthChip({ health }) {
    const errorRate = health.error_rate;
    const ok = health.eligible_for_promotion;
    const tone = ok
        ? "border-[#10b981]/40 bg-[#10b981]/10 text-[#6ee7b7]"
        : "border-white/15 bg-white/[0.04] text-white/55";
    return (
        <span
            data-testid="superadmin-release-canary-health"
            className={`ml-2 inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] ${tone}`}
            title={health.reason}
        >
            {ok ? <CheckCircle2 size={9} /> : <Clock size={9} />}
            {health.hours_observed.toFixed(1)}h · {errorRate == null ? "n/a" : `${(errorRate * 100).toFixed(1)}%`} err
        </span>
    );
}

/* ---- Canary settings card ----------------------------------------------- */
function CanarySettingsCard({ settings, onSaved }) {
    const [open, setOpen] = useState(false);
    const [percent, setPercent] = useState(settings.canary_percent ?? 0);
    const [tenantId, setTenantId] = useState(settings.canary_tenant_id ?? "");
    const [hours, setHours] = useState(settings.promotion_min_hours ?? 24);
    const [maxErr, setMaxErr] = useState((settings.promotion_max_error_rate ?? 0.05) * 100);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setPercent(settings.canary_percent ?? 0);
        setTenantId(settings.canary_tenant_id ?? "");
        setHours(settings.promotion_min_hours ?? 24);
        setMaxErr((settings.promotion_max_error_rate ?? 0.05) * 100);
    }, [settings]);

    const save = async () => {
        setSaving(true);
        try {
            await http.patch("/v1/agents/platform-settings", {
                canary_percent: Number(percent),
                canary_tenant_id: tenantId || "",
                promotion_min_hours: Number(hours),
                promotion_max_error_rate: Number(maxErr) / 100,
            });
            toast.success("Canary settings saved.");
            await onSaved?.();
            setOpen(false);
        } catch (e) {
            toast.error(errMsg(e, "Save failed"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mb-5 rounded-xl border border-[#a78bfa]/25 bg-gradient-to-br from-[#1e1b4b]/40 to-transparent p-4" data-testid="superadmin-canary-settings">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="flex w-full items-center justify-between"
                data-testid="superadmin-canary-settings-toggle"
            >
                <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#c4b5fd]">
                    <SettingsIcon size={12} /> Canary channel settings
                </span>
                <span className="font-mono text-[10px] text-white/40">{open ? "▾" : "▸"}</span>
            </button>
            {open && (
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label="Canary % of fleet" hint="Deterministically hashed per-agent. Sticky.">
                        <input
                            type="number" min={0} max={100} value={percent}
                            onChange={(e) => setPercent(e.target.value)}
                            data-testid="canary-percent-input"
                            className="w-full rounded-md border border-white/10 bg-black/30 px-3 py-2 font-mono text-sm text-white focus:border-[#a78bfa] focus:outline-none"
                        />
                    </Field>
                    <Field label="Pinned canary tenant ID" hint="Optional — pin a whole dogfood tenant.">
                        <input
                            type="text" value={tenantId}
                            onChange={(e) => setTenantId(e.target.value)}
                            placeholder="(none)"
                            data-testid="canary-tenant-input"
                            className="w-full rounded-md border border-white/10 bg-black/30 px-3 py-2 font-mono text-xs text-white focus:border-[#a78bfa] focus:outline-none"
                        />
                    </Field>
                    <Field label="Min observation window (hours)" hint="Before a canary can promote.">
                        <input
                            type="number" min={0} max={720} value={hours}
                            onChange={(e) => setHours(e.target.value)}
                            data-testid="canary-min-hours-input"
                            className="w-full rounded-md border border-white/10 bg-black/30 px-3 py-2 font-mono text-sm text-white focus:border-[#a78bfa] focus:outline-none"
                        />
                    </Field>
                    <Field label="Max acceptable error rate (%)" hint="Canary failed-job rate.">
                        <input
                            type="number" min={0} max={100} step={0.1} value={maxErr}
                            onChange={(e) => setMaxErr(e.target.value)}
                            data-testid="canary-max-err-input"
                            className="w-full rounded-md border border-white/10 bg-black/30 px-3 py-2 font-mono text-sm text-white focus:border-[#a78bfa] focus:outline-none"
                        />
                    </Field>
                    <div className="col-span-1 sm:col-span-2">
                        <button
                            type="button"
                            onClick={save}
                            disabled={saving}
                            data-testid="canary-settings-save"
                            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-r from-[#7c3aed] to-[#a78bfa] px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-white shadow-lg shadow-[#a78bfa]/25 transition hover:brightness-110 disabled:opacity-50"
                        >
                            {saving ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
                            Save
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <label className="block">
            <span className="block text-[10px] font-semibold uppercase tracking-[0.22em] text-white/45">{label}</span>
            <div className="mt-1.5">{children}</div>
            {hint && <span className="mt-1 block font-mono text-[10px] text-white/30">{hint}</span>}
        </label>
    );
}
